// web/src/components/ds/DsChipMenu.tsx — DS.6 (sprint aos-ds-composer, spec §2
// D4, BRANCH B — the default).
//
// A chip that OPENS A MENU while staying ONE interactive node. The trigger
// half is `DsChipPopover`'s (the measured reason it exists is documented
// there); this file adds the `role="menu"` list and the keyboard model DS.5's
// `DsMenu` established for the shell:
//
//   ArrowDown / ArrowUp  move between rows, wrapping
//   Enter / Space        activate — natively: the rows are real <button>s
//   Escape               closes AND returns focus to the trigger (DsChipPopover)
//   focus leaving        closes (tab-to-close)
//
// NOT implemented, inherited from DS.5's measured delta vs Radix
// `DropdownMenu` and ledgered in §6: typeahead, Home/End.
//
// `@ds/overlays/Menu` itself is NOT mounted: its `items[]` model puts the
// interaction on an inner `role="button"` div with no prop seam (the same
// defect DsChipPopover documents), so using it would cost the native
// `disabled` this chip's own unit test asserts. The upstream `asChild` issue
// is the exit; when `Menu` grows one, these rows become its `items[]`.
//
// BOTH the trigger and the menu container are render slots, and that is
// deliberate: `MenuItem` upstream carries no attribute slot at all, while the
// composer's rows are two-line icon+label+description blocks whose
// `data-testid`s are a contract (`mode-chip`, `mode-popover`,
// `mode-option-<mode>`). Handing the caller the props to spread keeps every id
// a LITERAL at the call site — where the campaign's testid census can see it,
// and where a reader looking for `mode-popover` will actually find it — while
// the adapter keeps the keyboard model and the open/close state.

import {
  createContext,
  useCallback,
  useContext,
  type ButtonHTMLAttributes,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';
import { DsChipPopover, type DsChipTriggerProps } from './DsChipPopover';

/** What the caller spreads onto its own `role="menu"` container. */
export interface DsChipMenuContainerProps {
  role: 'menu';
  style: CSSProperties;
  onKeyDown: (e: ReactKeyboardEvent<HTMLDivElement>) => void;
}

export interface DsChipMenuProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  disabled?: boolean;
  /** Named for the attribute it becomes — see DsChipPopover's note. */
  'data-testid': string;
  renderTrigger: (props: DsChipTriggerProps) => ReactNode;
  /** Renders the `role="menu"` container around the caller's `DsChipMenuRow`s. */
  renderMenu: (props: DsChipMenuContainerProps) => ReactNode;
}

const CloseContext = createContext<() => void>(() => {});

const menuStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  padding: 'var(--space-1) 0',
  outline: 'none',
};

const rowStyle: CSSProperties = {
  display: 'block',
  width: '100%',
  textAlign: 'left',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  padding: 'var(--space-2) var(--space-3)',
  color: 'var(--text-secondary)',
  fontFamily: 'var(--font-family-body)',
  // index.css floors every button at the 44 px touch target with zero
  // specificity; the rows size themselves from their content, exactly as the
  // Radix items they replace did.
  minHeight: 0,
  minWidth: 0,
};

function rowsOf(panel: HTMLElement): HTMLElement[] {
  return Array.from(panel.querySelectorAll<HTMLElement>('[role="menuitem"]'));
}

export interface DsChipMenuRowProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onSelect' | 'role' | 'type'> {
  onSelect: () => void;
}

/** One `role="menuitem"` row. Closes the menu, then runs `onSelect`. */
export function DsChipMenuRow({ onSelect, style, children, ...props }: DsChipMenuRowProps) {
  const close = useContext(CloseContext);
  return (
    <button
      type="button"
      role="menuitem"
      style={{ ...rowStyle, ...style }}
      onClick={() => {
        close();
        onSelect();
      }}
      {...props}
    >
      {children}
    </button>
  );
}

export function DsChipMenu({
  open,
  onOpenChange,
  disabled,
  'data-testid': triggerTestId,
  renderTrigger,
  renderMenu,
}: DsChipMenuProps) {
  const focusFirst = useCallback((panel: HTMLDivElement) => {
    rowsOf(panel)[0]?.focus();
  }, []);
  const close = useCallback(() => onOpenChange(false), [onOpenChange]);

  function onMenuKeyDown(e: ReactKeyboardEvent<HTMLDivElement>) {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const all = rowsOf(e.currentTarget);
    if (all.length === 0) return;
    const idx = all.indexOf(document.activeElement as HTMLElement);
    const delta = e.key === 'ArrowDown' ? 1 : -1;
    all[(idx + delta + all.length) % all.length]?.focus();
  }

  return (
    <DsChipPopover
      open={open}
      onOpenChange={onOpenChange}
      disabled={disabled}
      data-testid={triggerTestId}
      hasPopup="menu"
      renderTrigger={renderTrigger}
      onOpenAutoFocus={focusFirst}
    >
      <CloseContext.Provider value={close}>
        {renderMenu({ role: 'menu', style: menuStyle, onKeyDown: onMenuKeyDown })}
      </CloseContext.Provider>
    </DsChipPopover>
  );
}

export default DsChipMenu;
